/* /feedback — học viên gửi góp ý cho Simba và xem các góp ý đã gửi. */
import { useEffect, useState, type FormEvent } from "react"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Field, FieldGroup, FieldLabel } from "@/components/ui/field"
import { Input } from "@/components/ui/input"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"
import { Icon } from "@/components/icon"
import { EmptyState, ErrorState, LoadingState, Page, PageHeader, StatusNote, usePageTitle } from "@/components/page"
import { useSession } from "@/app/session"
import { api, loginUrl, post } from "@/lib/api"

const LABELS: Record<string, string> = {
  feature_request: "Bổ sung tính năng",
  feature_adjustment: "Điều chỉnh tính năng",
  bug_report: "Báo lỗi",
  learning_experience: "Trải nghiệm học tập",
  content: "Nội dung học liệu",
  other: "Góp ý khác",
}
const CATEGORY_ITEMS = Object.entries(LABELS).map(([value, label]) => ({ value, label }))
const EMPTY = { category: "feature_request", title: "", description: "" }

function FeedbackForm({ onDone, onCancel }: { onDone: () => void; onCancel: () => void }) {
  const [form, setForm] = useState(EMPTY)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState("")
  const update = (key: keyof typeof EMPTY, value: string) => setForm((f) => ({ ...f, [key]: value }))
  const save = async (e: FormEvent) => {
    e.preventDefault()
    setBusy(true)
    setError("")
    try {
      await post("/api/product-feedback", form)
      onDone()
    } catch (err: any) {
      setError(err.message)
      setBusy(false)
    }
  }
  return (
    <Card>
      <CardContent>
        <form onSubmit={save}>
          <FieldGroup>
            <Field>
              <FieldLabel>Loại feedback</FieldLabel>
              <Select items={CATEGORY_ITEMS} value={form.category} onValueChange={(v) => update("category", String(v ?? "feature_request"))}>
                <SelectTrigger className="w-full" aria-label="Loại feedback">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORY_ITEMS.map((i) => (
                    <SelectItem key={i.value} value={i.value}>
                      {i.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field>
              <FieldLabel htmlFor="fb-title">Tiêu đề</FieldLabel>
              <Input id="fb-title" required minLength={4} maxLength={120} value={form.title} onChange={(e) => update("title", e.target.value)} placeholder="Con muốn Simba thay đổi điều gì?" />
            </Field>
            <Field>
              <FieldLabel htmlFor="fb-desc">Nội dung</FieldLabel>
              <Textarea id="fb-desc" required minLength={10} maxLength={4000} rows={5} value={form.description} onChange={(e) => update("description", e.target.value)} placeholder="Kể ngắn gọn điều đang xảy ra và điều con mong muốn." />
            </Field>
            {error && <StatusNote tone="error">{error}</StatusNote>}
            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <Button type="button" variant="outline" onClick={onCancel}>
                Hủy
              </Button>
              <Button type="submit" disabled={busy}>
                <Icon name="send" /> {busy ? "Đang gửi…" : "Gửi feedback"}
              </Button>
            </div>
          </FieldGroup>
        </form>
      </CardContent>
    </Card>
  )
}

export default function FeedbackPage() {
  usePageTitle("Góp ý", "Gửi góp ý giúp Simba tốt hơn cho học viên Sư Tử Con.")
  const { loading, user } = useSession()
  const [requests, setRequests] = useState<any[] | null>(null)
  const [error, setError] = useState("")
  const [creating, setCreating] = useState(false)
  const [notice, setNotice] = useState("")

  const load = () => {
    setError("")
    return api("/api/product-feedback")
      .then((d) => setRequests(d?.requests || []))
      .catch((e: any) => {
        if (e.status === 401) location.href = loginUrl()
        else setError(e.message)
      })
  }
  useEffect(() => {
    if (loading) return
    if (!user) {
      location.href = loginUrl(`${location.origin}/feedback`)
      return
    }
    void load()
  }, [loading, user])

  if (loading || !user) return <LoadingState label="Đang mở khu vực góp ý…" />

  return (
    <Page className="max-w-3xl">
      <PageHeader
        eyebrow="Góp ý"
        title="Feedback của con"
        description="Điều gì làm con vướng, hay con ước Simba có thêm gì? Kể cho tụi mình nghe nhé."
        actions={
          !creating && (
            <Button
              onClick={() => {
                setCreating(true)
                setNotice("")
              }}
            >
              <Icon name="plus" /> Tạo feedback mới
            </Button>
          )
        }
      />
      {notice && <StatusNote tone="success">{notice}</StatusNote>}
      {creating ? (
        <FeedbackForm
          onCancel={() => setCreating(false)}
          onDone={() => {
            setCreating(false)
            setNotice("Đã gửi góp ý. Cảm ơn con đã cùng xây dựng Simba!")
            void load()
          }}
        />
      ) : error ? (
        <ErrorState message={error} onRetry={load} />
      ) : !requests ? (
        <LoadingState label="Đang tải góp ý…" />
      ) : requests.length ? (
        <ul className="flex flex-col gap-3" aria-label="Feedback đã gửi">
          {requests.map((item) => (
            <li key={item.id}>
              <Card size="sm">
                <CardContent className="flex flex-col gap-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge>{LABELS[item.category] || item.category}</Badge>
                    {item.status && <Badge variant="secondary">{item.status}</Badge>}
                  </div>
                  <span className="font-heading text-base font-semibold break-words text-ink">{item.title}</span>
                  <p className="text-sm break-words whitespace-pre-line text-muted-foreground">{item.description}</p>
                </CardContent>
              </Card>
            </li>
          ))}
        </ul>
      ) : (
        <EmptyState mascot="meerkat-thinking" title="Con chưa gửi feedback nào" description="Mọi góp ý, dù nhỏ, đều giúp Simba tốt hơn." />
      )}
    </Page>
  )
}
